// PriorityLinks.js
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink)}>
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};

const isPriorityLinkActive = (linkConfig, currentPage) => {
  if (!linkConfig || !currentPage) return false;

  const { type, route } = linkConfig;
  if (type !== 'internal' || !route) return false;

  const page = String(currentPage);
  const routeName = route?.name;

  // Most pages in the template
  if (routeName && routeName === page) return true;

  // CMS pages: currentPage might be "CMSPage", "about", or "CMSPage:about"
  if (routeName === 'CMSPage') {
    const pageId = route?.params?.pageId;
    if (page === 'CMSPage') return true;
    if (pageId && pageId === page) return true;
    if (pageId && page === `CMSPage:${pageId}`) return true;
  }

  return false;
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig, currentPage }) => {
  const { text, type, href, route, highlight } = linkConfig;

  const isActive = isPriorityLinkActive(linkConfig, currentPage);

  const classes = classNames(css.priorityLink, {
    [css.highlight]: highlight,
    [css.priorityLinkActive]: isActive,
  });

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }

  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done once before the real rendering and it's done outside the viewport.
  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks, currentPage, hideCreateListing } = props;
  const isServer = typeof window === 'undefined';
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);

  // Skip DRAWING the "Post a new listing" link for confirmed renters. This is a
  // render-only skip inside the map below -- we deliberately DO NOT filter the
  // `links`/`priorityLinks` arrays (they drive the one-shot width measurement).
  // Filtering those arrays creates a new reference every render and re-triggers
  // the measurement/setLinks cycle => infinite render loop / page freeze.
  const shouldSkip = linkConfig =>
    hideCreateListing && linkConfig?.route?.params?.type === 'new';

  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };

  const linkConfigs = isMeasured ? priorityLinks : links;

  const content = (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        // Render-only skip for renters. When measuring (off-screen, !isMeasured)
        // we still render the full set so widths stay correct; the skip only
        // hides the link in the real (measured) render.
        if (isMeasured && shouldSkip(linkConfig)) {
          return null;
        }
        return (
          <PriorityLink
            key={`${linkConfig.text}_${index}`}
            linkConfig={linkConfig}
            currentPage={currentPage}
          />
        );
      })}
    </div>
  );

  return isMeasured || isServer ? content : ReactDOM.createPortal(content, document.body);
};

export default PriorityLinks;
